import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(dt) {
  try {
    return new Date(dt).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return dt;
  }
}

const STATUS_BADGE = {
  paid: "bg-[#C6D21F] text-[#0D2F4D]",
  free: "bg-[#ECF7E6] text-[#3E7C2A]",
  initiated: "bg-[#ECF0F4] text-[#6F8FA8]",
  expired: "bg-white text-[#6F8FA8] border border-[#D6DCE3]",
};

export const SalesTab = ({ pw, type }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const load = () => {
    axios
      .get(`${API}/admin/sales`, { headers: { "X-Admin-Password": pw } })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load sales"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  const clearTest = async () => {
    if (!window.confirm("Remove ALL test rows, $0 orders and abandoned checkouts?\nReal paid sales are never touched.")) return;
    setClearing(true);
    try {
      const r = await axios.post(`${API}/admin/sales/clear-test`, {}, { headers: { "X-Admin-Password": pw } });
      toast.success(`Removed ${r.data.deleted} rows — kept ${r.data.kept_paid} real paid sales.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed.");
    } finally { setClearing(false); }
  };

  const removeRow = async (t) => {
    if (!window.confirm(`Delete this checkout row for ${t.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/sales/transactions/${t.session_id}`, { headers: { "X-Admin-Password": pw } });
      toast.success("Row removed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  if (loading) return <p className="text-center text-[#6F8FA8] py-20">Loading sales…</p>;
  if (!data) return null;

  const section = type === "guides" ? data.guides : data.packages;
  const noun = type === "guides" ? "guide" : "package";

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={clearTest}
          disabled={clearing}
          data-testid="sales-clear-test"
          className="text-xs font-bold px-4 py-2 rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
        >
          {clearing ? "Cleaning…" : "Clear test & abandoned checkouts"}
        </button>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {[
          { k: "revenue", label: "Revenue (AUD)", v: `$${section.revenue.toLocaleString()}` },
          { k: "paid", label: "Paid sales", v: section.paid_count },
          { k: "started", label: "Checkouts started", v: section.started_count },
        ].map((s) => (
          <div key={s.k} data-testid={ADMIN.salesStat(`${type}-${s.k}`)} className="bg-white rounded-2xl p-5 border border-[#E4E7EA]">
            <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">{s.label}</p>
            <p className="font-display text-4xl font-extrabold text-[#0D2F4D] mt-2">{s.v}</p>
          </div>
        ))}
      </div>

      {/* Breakdown by product */}
      {Object.keys(section.breakdown).length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-[#E4E7EA] mb-6">
          <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mb-3">
            Sales by {noun}
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(section.breakdown).map(([id, b]) => (
              <span key={id} className="text-sm font-semibold px-3 py-1.5 rounded-full bg-[#ECF0F4] text-[#0D2F4D]">
                {b.title} · {b.count} {b.count === 1 ? "sale" : "sales"} · ${b.revenue.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Provider split — Stripe vs PayPal */}
      {section.providers && Object.keys(section.providers).length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-[#E4E7EA] mb-6" data-testid={`${type}-providers`}>
          <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mb-3">
            Payment provider split
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(section.providers).map(([prov, p]) => (
              <span
                key={prov}
                data-testid={`provider-${prov}`}
                className="text-sm font-semibold px-3 py-1.5 rounded-full bg-[#0D2F4D] text-[#C6D21F] uppercase tracking-widest text-xs"
              >
                {prov} · {p.count} · ${p.revenue.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Transactions */}
      {section.transactions.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
          <p className="text-[#6F8FA8]">No {noun} checkouts yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {section.transactions.map((t) => (
            <div
              key={t.session_id}
              data-testid={ADMIN.salesRow(t.session_id)}
              className="bg-white rounded-2xl px-6 py-4 border border-[#E4E7EA] flex flex-wrap items-center gap-x-6 gap-y-2"
            >
              <div className="min-w-[180px]">
                <p className="font-display font-bold text-[#0D2F4D]">
                  {t.first_name} {t.last_name || ""}
                </p>
                <a href={`mailto:${t.email}`} className="text-sm text-[#6F8FA8] hover:text-[#0D2F4D]">
                  {t.email}
                </a>
              </div>
              <p className="text-sm text-[#0D2F4D] font-semibold flex-1 min-w-[160px]">
                {t.guide_title || t.package_title}
              </p>
              <p className="text-xs text-[#6F8FA8]">{fmtDate(t.created_at)}</p>
              <span
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                  STATUS_BADGE[t.payment_status] || STATUS_BADGE.initiated
                }`}
              >
                {t.payment_status === "paid" ? `PAID · $${(t.amount || 0).toLocaleString()}` : (t.payment_status || "—").toUpperCase()}
              </span>
              {!(t.payment_status === "paid" && (t.amount || 0) > 0) && (
                <button
                  onClick={() => removeRow(t)}
                  data-testid={`sales-delete-${t.session_id}`}
                  title="Delete this row"
                  className="text-[#94A3B8] hover:text-red-500 text-lg leading-none font-bold px-1"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
